import { Save } from "lucide-react";
import {
  EditBase,
  Form,
  useDataProvider,
  useNotify,
  useTranslate,
} from "ra-core";
import type { ComponentType } from "react";
import { useFormState } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";

import { useConfigurationUpdater } from "../root/ConfigurationContext";
import {
  sectionConfigUpdate,
  useSettingsDefaultValues,
  type SettingsConfigKey,
} from "./settingsForm";
import {
  SETTINGS_SECTIONS,
  type SettingsSectionId,
} from "./settingsSectionRegistry";

/**
 * One section of Settings in a dialog, so a vocabulary can be edited from the
 * place that uses it (Bill, meeting 476: edit record types without hunting
 * through Settings). The section is the same component the Settings page
 * renders; only what surrounds it differs. LCCRM-86.
 */
export const SettingsSectionDialog = ({
  section,
  open,
  onOpenChange,
}: {
  section: SettingsSectionId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const { Component, configKeys, label, fallback } = SETTINGS_SECTIONS[section];
  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl max-h-[85vh] overflow-y-auto"
        // The section draws its own visible title and help text.
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">
          {translate(label, { smart_count: 2, _: fallback })}
        </DialogTitle>
        {configKeys ? (
          <ConfigSectionForm
            Component={Component}
            configKeys={configKeys}
            onDone={close}
          />
        ) : (
          // Saves each change as it is made, so there is nothing to submit.
          <>
            <Component />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                {translate("ra.action.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ConfigSectionForm = ({
  Component,
  configKeys,
  onDone,
}: {
  Component: ComponentType;
  configKeys: readonly SettingsConfigKey[];
  onDone: () => void;
}) => {
  const dataProvider = useDataProvider();
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();
  const defaultValues = useSettingsDefaultValues();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      // Re-read the row at save time and change only this section's keys:
      // see sectionConfigUpdate for why the context is not good enough.
      transform={async (data: Record<string, any>) => {
        const { data: stored } = await dataProvider.getOne("configuration", {
          id: 1,
        });
        return sectionConfigUpdate(stored?.config, data, configKeys);
      }}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
          onDone();
        },
        onError: () => {
          notify("crm.settings.save_error", { type: "error" });
        },
      }}
    >
      {/* React bubbles a submit through the dialog's portal to whatever form
          the settings icon sits in. Stop it here so only this form saves. */}
      <div onSubmit={(event) => event.stopPropagation()}>
        <Form defaultValues={defaultValues} className="grid gap-4">
          <Component />
          <SectionFormFooter onCancel={onDone} />
        </Form>
      </div>
    </EditBase>
  );
};

const SectionFormFooter = ({ onCancel }: { onCancel: () => void }) => {
  const translate = useTranslate();
  const { isSubmitting } = useFormState();
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        {translate("ra.action.cancel")}
      </Button>
      <Button type="submit" disabled={isSubmitting}>
        <Save className="h-4 w-4 mr-1" />
        {isSubmitting
          ? translate("crm.settings.saving")
          : translate("ra.action.save")}
      </Button>
    </DialogFooter>
  );
};
